import React from 'react'
import styles from './deviceTerminal.jcss'
import Icon from 'blocks.simple/icon/icon'
import { DeviceTerminalMethods } from './DeviceTerminalMethods'
import { cn } from 'ethcss'
import DotTooltip from 'blocks.simple/dotTooltip/dotTooltip'
import translate from 'core/translate'
import { IDevice } from 'core/models/DigitalSignage'
import EditText from 'blocks.simple/editText/editText'
import Button from 'blocks.simple/button/button'
import KeyboardEventHandler from 'react-keyboard-event-handler'

export const DeviceTerminal = ({ device, onClose }: { device: IDevice; onClose: () => void }) => {
    const {
        onMinimize,
        onMaximize,
        windowStyles,
        minimize,
        maximize,
        onDragStart,
        onMouseDown,
        onTouchStart,
        monitorRef,
        resizerLeftRef,
        resizerRightRef,
        resizerTopRef,
        resizerBottomRef,
        onSend,
        inputString,
        setInputString,
        terminalLogs,
        directory,
        sendInProgress,
        onCancel,
    } = DeviceTerminalMethods({ device })

    const getTitle = () => (
        <DotTooltip
            tooltip={{
                html: device.name,
                position: 'left',
                disabled: false,
                followCursor: true,
            }}
            containerClass={cn(styles.menuItem__text)}
        >
            <div className={styles.title}>
                {!minimize && <b>{translate('deviceTerminal')}: </b>}
                {device.name}
            </div>
        </DotTooltip>
    )

    const renderLogs = () => terminalLogs.join('\n')

    return (
        <div
            ref={monitorRef}
            className={styles.monitorWindow}
            style={windowStyles}
            onMouseDown={(e) => {
                if (!maximize) {
                    onMouseDown(e)
                }
            }}
            onTouchStart={(e) => {
                if (!maximize) {
                    onTouchStart(e)
                }
            }}
            onDragStart={onDragStart}
        >
            {/* Resizers */}
            <div
                ref={resizerLeftRef}
                className={cn(styles.resizer, styles.resizer_l, minimize || maximize ? styles.noDraggable : '')}
            />
            <div
                ref={resizerTopRef}
                className={cn(styles.resizer, styles.resizer_t, minimize || maximize ? styles.noDraggable : '')}
            />
            <div
                ref={resizerRightRef}
                className={cn(styles.resizer, styles.resizer_r, minimize || maximize ? styles.noDraggable : '')}
            />
            <div
                ref={resizerBottomRef}
                className={cn(styles.resizer, styles.resizer_b, minimize || maximize ? styles.noDraggable : '')}
            />

            {/* Header */}
            <div className={styles.header}>
                {getTitle()}
                <div className={styles.actions}>
                    <div className={cn(styles.action, minimize ? styles.disabled : '')} onClick={onMaximize}>
                        <Icon
                            type={'maximize_hollow'}
                            size={14}
                            color={'white'}
                            tooltip={{
                                title: translate(maximize ? 'restore' : 'fullScreen'),
                            }}
                        />
                    </div>
                    <div className={styles.action} onClick={onMinimize}>
                        <Icon
                            type={'minimize_line'}
                            size={14}
                            color={'white'}
                            tooltip={{
                                title: translate(minimize ? 'maximize' : 'minimize'),
                            }}
                        />
                    </div>
                    <div
                        className={styles.action}
                        onClick={() => {
                            if (sendInProgress) {
                                onCancel()
                            }
                            onClose()
                        }}
                    >
                        <Icon
                            type={'close_tiny'}
                            size={14}
                            color={'white'}
                            tooltip={{
                                title: translate('close'),
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className={cn(styles.content, minimize ? styles.hidden : '')} onMouseDown={(e) => e.stopPropagation()}>
                <textarea value={renderLogs()} className={styles.textArea} readOnly />
            </div>

            {/* Bottom Panel */}
            <div className={styles.bottomPanel}>
                <div className={styles.directory}>{directory}</div>
                <div className={styles.commandInput}>
                    <form
                        className={styles.form}
                        onSubmit={(e) => {
                            e.preventDefault()
                            if (!sendInProgress) {
                                onSend()
                            }
                        }}
                    >
                        <EditText value={inputString} onChange={setInputString} containerClass={styles.input} />
                    </form>
                    <Button mod={'fill'} onClick={sendInProgress ? onCancel : onSend}>
                        {translate(sendInProgress ? 'cancel' : 'send')}
                    </Button>
                </div>
            </div>

            {/* Keyboard Handler */}
            <KeyboardEventHandler
                handleKeys={['ctrl+c']}
                onKeyEvent={(key: string) => {
                    if (key === 'ctrl+c' || key === 'ctrl+с') {
                        onCancel()
                    }
                }}
            />
        </div>
    )
}
